import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PlusCircleIcon, UploadIcon, AlertCircleIcon } from "lucide-react";
import ProductAddForm from "@/polymet/components/product-add-form";
import BulkProductUpload from "@/polymet/components/bulk-product-upload";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth-context";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuthDebug } from "@/lib/auth-debug";

// Global component registry that survives all re-renders
const globalComponentRegistry = new Map<string, React.ReactElement>();

function getOrCreateComponent(key: string, factory: () => React.ReactElement): React.ReactElement {
  if (!globalComponentRegistry.has(key)) {
    const component = factory();
    globalComponentRegistry.set(key, component);
    console.log(`🔧 Created global component: ${key}`);
  }
  return globalComponentRegistry.get(key)!;
}

export default function AddProductPage() {
  const [activeTab, setActiveTab] = useState<string>("single");
  const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Debug auth context changes
  useAuthDebug("AddProductPage");
  
  // Get truly persistent components from global registry
  const productForm = getOrCreateComponent('add-product-form', () => 
    <ProductAddForm key="global-product-form" />
  );
  
  const bulkUpload = getOrCreateComponent('bulk-upload-form', () => 
    <BulkProductUpload key="global-bulk-upload" />
  );
  
  console.log('🔄 AddProductPage render - activeTab:', activeTab, 'user:', user?.id);
  
  useEffect(() => {
    // Check if user is logged in and has supplier privileges
    const checkAuthorization = async () => {
      setLoading(true);
      
      if (!user) {
        setIsAuthorized(false);
        setLoading(false);
        return;
      }
      
      try {
        // Check if the user is a supplier
        const { data: profile, error: profileError } = await supabase
          .from('accounts')
          .select('user_type')
          .eq('id', user.id)
          .single();
        
        if (profileError) throw profileError;
        
        // Only suppliers or users with both roles can add products
        setIsAuthorized(profile?.user_type === 'supplier' || profile?.user_type === 'both');
      } catch (error) {
        console.error('Error checking authorization:', error);
        setIsAuthorized(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuthorization();
  }, [user]);
  
  // Redirect unauthorized users
  useEffect(() => {
    if (!loading && !isAuthorized && !user) {
      navigate('/login?redirect=/add-product');
    }
  }, [loading, isAuthorized, user, navigate]);
  
  if (loading) {
    return <div className="container mx-auto px-4 py-8">Loading...</div>;
  }
  
  if (!isAuthorized && user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert variant="destructive" className="mb-6">
          <AlertCircleIcon className="h-4 w-4" />
          <AlertDescription>
            You must be a registered supplier to add products. Please contact support if you believe this is an error.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold">Add Product</h1>
          <p className="text-muted-foreground">
            Add a new machinery product to your inventory
          </p>
        </div>
      </div>

      <Card className="mb-8">
        <CardHeader className="pb-3">
          <CardTitle>Choose Upload Method</CardTitle>
          <CardDescription>
            Select how you want to add products to your inventory
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Tab Navigation */}
          <div className="grid w-full grid-cols-2 mb-8 bg-muted p-1 rounded-lg">
            <button
              onClick={() => setActiveTab("single")}
              className={`flex items-center justify-center gap-2 py-2 px-4 rounded-md transition-colors ${
                activeTab === "single"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <PlusCircleIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Single Product</span>
              <span className="sm:hidden">Single</span>
            </button>
            <button
              onClick={() => setActiveTab("bulk")}
              className={`flex items-center justify-center gap-2 py-2 px-4 rounded-md transition-colors ${
                activeTab === "bulk"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <UploadIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Bulk Upload</span>
              <span className="sm:hidden">Bulk</span>
            </button>
          </div>

          {/* Tab Content with Description */}
          <div className="bg-muted/50 p-4 rounded-lg mb-6">
            {activeTab === "single" ? (
              <>
                <h3 className="font-medium mb-2">Single Product Addition</h3>
                <p className="text-sm text-muted-foreground">
                  Use this method to add one product at a time with detailed
                  information. This is ideal for unique machinery or when you
                  have a small inventory.
                </p>
              </>
            ) : (
              <>
                <h3 className="font-medium mb-2">Bulk Product Upload</h3>
                <p className="text-sm text-muted-foreground">
                  Use this method to add multiple products at once using an
                  Excel file. This is ideal for large inventories or when
                  migrating from another system.
                </p>
              </>
            )}
          </div>

          {/* Persistent Components - Both are always rendered but only one is visible */}
          <div style={{ display: activeTab === "single" ? "block" : "none" }}>
            {productForm}
          </div>
          <div style={{ display: activeTab === "bulk" ? "block" : "none" }}>
            {bulkUpload}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
